import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, CheckCircle, Clock, AlertTriangle, FileText, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const ResultsPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    if (!requestId) {
      console.error('No request ID found in URL');
      toast.error('Invalid results URL - no request ID found');
      // Don't navigate away immediately, let user see the error
      setLoading(false);
      return;
    }

    console.log('Loading results for request ID:', requestId);

    // Start polling for status
    const pollStatus = async () => {
      try {
        console.log('Fetching status for:', requestId);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analysis/${requestId}/status`, {
          headers: {
          }
        });
        
        console.log('Status response:', response.status, response.statusText);
        
        if (!response.ok) {
          if (response.status === 404) {
            console.error('Analysis not found:', requestId);
            toast.error('Analysis request not found. This analysis may have been cleaned up or the ID is incorrect.');
            setLoading(false);
            setStatus('not_found'); // Set status to stop polling
            return;
          }
          throw new Error(`HTTP ${response.status}: Failed to fetch status`);
        }

        const data = await response.json();
        console.log('Status data:', data);
        setStatus(data.status);
        setResult(data.result || null);
        setLoading(false);

        // Update progress based on status
        switch (data.status) {
          case 'pending':
            setProgress(10);
            break;
          case 'processing':
            setProgress(30);
            break;
          case 'analyzing_speaker':
            // Show progress based on speaker analysis
            if (data.progress && data.progress.includes('Speaker')) {
              // Extract speaker progress like "Speaker 2/3: John"
              const match = data.progress.match(/Speaker (\d+)\/(\d+)/);
              if (match) {
                const [, current, total] = match;
                const speakerProgress = Math.round((parseInt(current) / parseInt(total)) * 60) + 30; // 30-90% range
                setProgress(Math.min(90, speakerProgress));
              } else {
                setProgress(60);
              }
            } else {
              setProgress(60);
            }
            break;
          case 'processing_chunks':
            // Calculate progress based on chunk completion
            if (data.progress && data.progress.includes('/')) {
              const [current, total] = data.progress.split('/').map(Number);
              const chunkProgress = Math.round((current / total) * 70) + 20; // 20-90% range
              setProgress(Math.min(90, chunkProgress));
            } else {
              setProgress(50);
            }
            break;
          case 'completed':
            setProgress(100);
            return; // Stop polling
          case 'failed':
            setProgress(0);
            return; // Stop polling
          default:
            setProgress(25);
        }

        // Continue polling if not completed, failed, or not found
        if (data.status !== 'completed' && data.status !== 'failed' && status !== 'not_found') {
          setTimeout(pollStatus, 3000); // Poll every 3 seconds
        }
      } catch (error) {
        console.error('Status check error:', error);
        setLoading(false);
        
        // Don't navigate away on API errors - could be temporary
        if (status === 'pending' || status === 'processing' || status === 'analyzing_speaker') {
          console.log('Temporary error during processing, will retry...');
          toast.error('Connection issue - retrying...', { duration: 2000 });
          // Continue polling after a longer delay
          setTimeout(pollStatus, 10000); // 10 seconds instead of 3
        } else {
          toast.error('Failed to check analysis status. Please refresh the page.');
        }
      }
    };

    pollStatus();
  }, [requestId, navigate, status]);

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analysis/${requestId}/report`);
      
      if (!response.ok) {
        throw new Error(`Failed to download report: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'coercive_control_analysis_report.pdf';
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded successfully!', { duration: 3000 });
      }, 100);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report. Please try again.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const getStatusInfo = () => {
    switch (status) {
      case 'pending':
        return {
          icon: <Clock className="w-8 h-8 text-yellow-500" />,
          title: 'Analysis Queued',
          description: 'Your analysis request has been received and is waiting to be processed.',
          color: 'yellow'
        };
      case 'analyzing_speaker':
        return {
          icon: <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />,
          title: 'Analyzing Individual Speakers',
          description: 'Analyzing each person\'s communication patterns separately for detailed insights. This may take 15-30 minutes for large documents.',
          color: 'blue',
          showEmailNotice: true
        };
      case 'processing_chunks':
        return {
          icon: <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />,
          title: 'Analyzing Document Chunks',
          description: 'Breaking down large document and analyzing each section for patterns of coercive control. This may take 15-30 minutes for large documents.',
          color: 'blue',
          showEmailNotice: true
        };
      case 'completed':
        return {
          icon: <CheckCircle className="w-8 h-8 text-green-500" />,
          title: 'Analysis Complete',
          description: 'Your analysis has been completed successfully. You can now download your report.',
          color: 'green'
        };
      case 'failed':
        return {
          icon: <AlertTriangle className="w-8 h-8 text-red-500" />,
          title: 'Analysis Failed',
          description: 'There was an error processing your analysis. Please try again or contact support.',
          color: 'red'
        };
      case 'not_found':
        return {
          icon: <AlertTriangle className="w-8 h-8 text-orange-500" />,
          title: 'Analysis Not Found',
          description: 'This analysis request could not be found. It may have been cleaned up or the ID is incorrect. Please start a new analysis.',
          color: 'orange'
        };
      default:
        return {
          icon: <RefreshCw className="w-8 h-8 text-gray-500 animate-spin" />,
          title: 'Processing',
          description: 'Processing your analysis request...',
          color: 'gray'
        };
    }
  };

  const getRiskScoreClass = (score) => {
    switch (score?.toLowerCase()) {
      case 'low':
        return 'risk-low';
      case 'moderate':
        return 'risk-moderate';
      case 'high':
        return 'risk-high';
      case 'extreme':
        return 'risk-extreme';
      default:
        return 'risk-low';
    }
  };

  const statusInfo = getStatusInfo();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analysis status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <h1 className="text-2xl font-bold gradient-text serif-display">Analysis Results</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Status Card */}
        <div className="card mb-8">
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              {statusInfo.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{statusInfo.title}</h2>
              <p className="text-gray-600 mb-4">{statusInfo.description}</p>
              
              {/* Progress Bar */}
              <div className="progress-bar mb-2">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">{progress}% complete</p>
            </div>
            
            {status === 'completed' && (
              <div className="flex-shrink-0 flex flex-col items-end">
                <button
                  onClick={downloadReport}
                  disabled={downloadingReport}
                  className="btn-primary flex items-center space-x-2"
                >
                  {downloadingReport ? (
                    <>
                      <div className="spinner"></div>
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Download Report</span>
                    </>
                  )}
                </button>
                {/* Privacy Warning */}
                <div className="mt-3 max-w-xs">
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    <span className="font-semibold">⚠️ Important:</span> For security and privacy purposes, this report can only be downloaded once and will then be automatically deleted. Please save a copy for your records.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Summary (only show when completed) */}
        {status === 'completed' && result && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-semibold">Analysis Summary</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Pages:</span>
                  <span className="font-semibold">{result.total_pages}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Instances:</span>
                  <span className="font-semibold">{result.total_instances}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Risk Score:</span>
                  <span className={`font-semibold ${getRiskScoreClass(result.risk_score)}`}>
                    {result.risk_score}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3 mb-4">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-semibold">Risk Assessment</h3>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">
                  {result.post_separation_abuse_score}/10
                </div>
                <p className="text-gray-600">
                  Likelihood of Post-Separation Abuse
                </p>
                <div className="mt-4 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-yellow-400 to-red-500 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${(result.post_separation_abuse_score / 10) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processing Information */}
        {(status === 'pending' || status === 'processing') && (
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">What's Happening Now</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${progress >= 10 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={progress >= 10 ? 'text-gray-900' : 'text-gray-500'}>
                  Analysis request received and queued
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${progress >= 25 ? 'bg-green-500' : progress >= 10 ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                <span className={progress >= 25 ? 'text-gray-900' : progress >= 10 ? 'text-blue-600' : 'text-gray-500'}>
                  Extracting text from uploaded content
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${progress >= 75 ? 'bg-green-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                <span className={progress >= 75 ? 'text-gray-900' : progress >= 50 ? 'text-blue-600' : 'text-gray-500'}>
                  AI models analyzing communication patterns
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${progress >= 100 ? 'bg-green-500' : progress >= 75 ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                <span className={progress >= 100 ? 'text-gray-900' : progress >= 75 ? 'text-blue-600' : 'text-gray-500'}>
                  Generating professional PDF report
                </span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Estimated processing time:</strong> 5-10 minutes for small files and an hour+ for larger files (500+ pages). 
                This page will automatically update when your analysis is complete.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'failed' && (
          <div className="card bg-red-50 border-red-200">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-900 mb-4">Analysis Failed</h3>
              <p className="text-red-700 mb-6">
                We encountered an error while processing your analysis. This could be due to:
              </p>
              <ul className="text-left text-red-700 mb-6 space-y-2">
                <li>• Unsupported file format or corrupted file</li>
                <li>• Content that couldn't be processed</li>
                <li>• Temporary system issues</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/upload')}
                  className="btn-primary"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn-secondary"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Preview (only show when completed) */}
        {status === 'completed' && result && (
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Report Preview</h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="text-center mb-6">
                <h4 className="text-lg font-bold">📊 Make the Invisible Visible — Summary of Findings</h4>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h5 className="font-semibold mb-2">Case Summary</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total Pages Analyzed:</span>
                      <span>{result.total_pages}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Instances:</span>
                      <span>{result.total_instances}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Overall Risk Score:</span>
                      <span className={getRiskScoreClass(result.risk_score)}>
                        {result.risk_score}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Post-Separation Abuse:</span>
                      <span>{result.post_separation_abuse_score}/10</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-semibold mb-2">Analysis Categories</h5>
                  <p className="text-sm text-gray-600">
                    The full report includes detailed breakdowns of all 19 coercive control categories 
                    with specific references and risk flag indicators.
                  </p>
                </div>
              </div>
              
              <div className="text-center">
              {/* Download Method */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={downloadReport}
                  disabled={downloadingReport}
                  className="btn-primary flex items-center space-x-2 mx-auto"
                >
                  {downloadingReport ? (
                    <>
                      <div className="spinner"></div>
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Download Report</span>
                    </>
                  )}
                </button>
              </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;